<template>
    <div class="container panel">
        <section class="section">
            <div class="column is-10-desktop is-offset-1-desktop is-10 is-offset-1">
                <div class="buttons has-addons">
                    <button class="button" @click="back">
                        <b-icon icon="arrow-left" size="is-small"></b-icon>
                        <span>{{ $t('button.back') }}</span>
                    </button>
                </div>
                <b-tabs type="is-boxed">
                    <b-tab-item :label="$t('title.info').toString()" icon="information-outline" class="custom-tab-item">
                        <h1 class="title">{{ $t('title.addMembership') }}</h1>

                        <validation-observer v-slot="{ passes }">
                            <div class="columns">
                                <div class="column is-6">
                                    <AutoCompleteField
                                        required
                                        name="customer"
                                        v-model="local.row.customerId"
                                        resource="customer"
                                        field="firstName"
                                        :query="customersSelectorParams"
                                        :disabled="!isAdmin"
                                    ></AutoCompleteField>
                                </div>
                            </div>
                            <div class="columns">
                                <div class="column is-6">
                                    <SelectField
                                        v-model="local.row.productType"
                                        name="type"
                                        :options="local.productTypeOptions"
                                        :required="true"
                                        expanded
                                        :disabled="!isAdmin"
                                    />
                                </div>
                                <div class="column is-6">
                                    <SelectField
                                        v-model="local.row.priceType"
                                        name="priceType"
                                        :options="filteredPriceTypeOptions"
                                        :required="true"
                                        expanded
                                        :disabled="!isAdmin"
                                    />
                                </div>
                            </div>
                            <div class="columns">
                                <div class="column is-6" v-if="local.row.startDate">
                                    <DateField
                                        v-model="local.row.startDate"
                                        name="startDate"
                                        required
                                        :disabled="!isAdmin"
                                    />
                                </div>
                                <div class="column is-6">
                                    <DateField v-model="local.row.endDate" :disabled="!isAdmin" />
                                </div>
                            </div>
                            <div class="columns">
                                <div class="column is-12">
                                    <TextAreaField v-model="local.row.notes" :disabled="!isAdmin" />
                                </div>
                            </div>
                            <button
                                class="button is-primary is-fullwidth login-button space-2"
                                @click="passes(add)"
                                :disabled="!passes"
                            >
                                <b-icon icon="plus-circle-outline"></b-icon>
                                <span>{{ $t('button.add') }}</span>
                            </button>
                            <button
                                class="button is-primary is-fullwidth login-button space-1"
                                @click="passes(addAndClose)"
                                :disabled="!passes"
                            >
                                <b-icon icon="plus-circle-outline"></b-icon>
                                <span>{{ $t('button.addAndClose') }}</span>
                            </button>
                        </validation-observer>
                    </b-tab-item>
                </b-tabs>
            </div>
        </section>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';

import TextField from '../../../../framework/fields/TextField.vue';
import SelectField from '../../../../framework/fields/SelectField.vue';
import DateField from '../../../../framework/fields/DateField.vue';
import TextAreaField from '../../../../framework/fields/TextAreaField.vue';

import { sharedState } from '../../../../framework/state';
import { postResource } from '../../../../framework/client/resource';

import { ApplicationResource } from '../../../../../common/application/enumeration/ApplicationResource';

import { successToast, errorToast } from '../../../service/toast_service';
import { FrameworkUserRole } from '../../../../../common/framework/enumeration/FrameworkUserRole';
import AutoCompleteField from '../../../../framework/fields/AutoCompleteField.vue';
import { Membership } from '../../../../../common/application/model/membership';
import { getCustomersSelectorParams, getProductsSelectorParams } from '../../../service/helper_service';
import moment from 'moment';
import { Option } from '../../../service/application_options_service';
import { getDatabaseOptions, getEnumerationOptions, OptionValue } from '../../../../framework/service/options';
import { ProductType } from '../../../../../common/application/model/enums/product_type';

@Component({
    components: {
        TextAreaField,
        SelectField,
        TextField,
        ValidationObserver,
        AutoCompleteField,
        DateField,
    },
})
export default class AddMembership extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.MEMBERSHIP;

    // Properties
    @Prop(String) readonly customerId!: string;

    // State
    shared = sharedState;
    local = {
        row: {
            customerId: this.customerId,
            startDate: moment().add(1, 'month').startOf('month').toDate(),
            notes: null,
            endDate: null,
            priceType: 'new-individual',
        } as Membership,
        productTypeOptions: [] as Option[],
        priceTypeOptions: [] as OptionValue[],
    };

    async mounted() {
        this.local.productTypeOptions = getEnumerationOptions('ProductType', Object.keys(ProductType), this).filter(
            (option) =>
                option.id === ProductType.MEMBERSHIP ||
                option.id === ProductType.MASSAGE ||
                option.id === ProductType.HYBRID_MEMBERSHIP ||
                option.id === ProductType.MANAGEMENT_COACHING ||
                option.id === ProductType.ONLINE_COACHING ||
                option.id === ProductType.SUPERCELL_MEMBERSHIP ||
                option.id === ProductType.VOUCHER_MEMBERSHIP,
        );

        this.local.priceTypeOptions = await getDatabaseOptions('MembershipPriceType', this);
    }

    add() {
        this.addResource(false);
    }

    addAndClose() {
        this.addResource(true);
    }

    async addResource(goBack: boolean) {
        try {
            const newMembership = await postResource<Membership>(this.resourceType, this.local.row);
            successToast(this, 'message.membershipSaved');

            if (goBack) {
                this.back();
            } else {
                await this.$router.replace(`/${this.resourceType}/${newMembership.id}`);
            }
        } catch (error) {
            errorToast(this);
        }
    }

    get isAdmin(): boolean {
        return this.shared.hasRole(FrameworkUserRole.ADMIN);
    }

    back() {
        this.$router.go(-1);
    }

    get customersSelectorParams() {
        return getCustomersSelectorParams(this.shared.context.scopes, this.local.row);
    }

    get productSelectorParams() {
        return getProductsSelectorParams(this.shared.context.scopes, this.local.row);
    }

    get filteredPriceTypeOptions() {
        if (this.local.row.productType === ProductType.MANAGEMENT_COACHING) {
            return this.local.priceTypeOptions.filter((o) => o.id !== 'new-individual-2025');
        }
        return this.local.priceTypeOptions;
    }

    @Watch('local.row.productType')
    onProductTypeChange(newType: ProductType) {
        if (newType === ProductType.MANAGEMENT_COACHING) {
            (this.local.row as any).priceType = 'new-individual';
        }
    }
}
</script>
